import React from "react"
import { Link } from "gatsby"
import {
  SemiBdTitle3,
  SemiBdTitle5,
  SemiBdTitle4,
  IconChevronRight,
  iconMapper,
  IconName,
} from "@sr/gatsby-theme-ui"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"

export interface ProductEntryRelatedProductsProps {
  relatedProducts: {
    on: boolean
    header: string
    productList: { title: string; icon: IconName; path: { current: string } }[]
  }
  pageTitle: string
}

const ProductEntryRelatedProducts = ({
  relatedProducts,
  pageTitle = "Product",
}: ProductEntryRelatedProductsProps) => {
  if (!relatedProducts?.on || !relatedProducts?.productList?.length) return null

  return (
    <ProductEntryRelatedProductsStyled>
      <SemiBdTitle3 className="product-entry-related-products__header">
        {pageTitle} pairs well with
      </SemiBdTitle3>
      <div className="product-entry-related-products__cards-container">
        {relatedProducts?.productList?.length
          ? relatedProducts.productList.map((product: any, i: number) => {
              return (
                <Link to={product.path.current} key={i}>
                  <div className="product-entry-related-products__card-container">
                    <div className="product-entry-related-products__card-icon">
                      {iconMapper(product?.icon, {
                        color: "var(--color-primary)",
                        size: 70,
                      })}
                    </div>
                    <SemiBdTitle5 className="product-entry-related-products__card-title">
                      {product.title}
                    </SemiBdTitle5>
                  </div>
                </Link>
              )
            })
          : null}
      </div>
      <div className="product-entry-related-products__link-container">
        <Link to="/products/" className="product-entry-related-products__link">
          <div className="product-entry-related-products__link-icon-container">
            <IconChevronRight color="var(--color-primary)" />
          </div>
          <SemiBdTitle4 className="product-entry-related-products__link-text">
            View all solutions
          </SemiBdTitle4>
        </Link>
      </div>
    </ProductEntryRelatedProductsStyled>
  )
}

export default ProductEntryRelatedProducts

const ProductEntryRelatedProductsStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${rem(152)} ${rem(16)} 0;

  @media ${media.midUp} {
    padding-top: ${rem(184)};
  }

  .product-entry-related-products__header {
    max-width: ${rem(251)};

    @media ${media.midUp} {
      max-width: ${rem(407)};
      font-size: ${rem(40)};
      line-height: ${rem(48)};
    }

    @media ${media.stMidUp} {
      max-width: ${rem(683)};
    }
  }

  .product-entry-related-products__cards-container {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
    padding-top: ${rem(48)};

    a {
      text-decoration: none;
    }

    @media ${media.midUp} {
      flex-direction: row;
      padding-top: ${rem(56)};
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(80)};
      gap: ${rem(56)};
    }

    .product-entry-related-products__card-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: ${rem(40)};
      width: ${rem(343)};
      height: ${rem(288)};
      border: ${rem(1)} solid var(--color-gray200);
      border-radius: ${rem(8)};
      transition: 0.2s;

      &:hover {
        box-shadow: 0px 4px 8px rgba(45, 50, 57, 0.2);
      }

      @media ${media.midUp} {
        width: ${rem(216)};
        height: ${rem(288)};
      }

      .product-entry-related-products__card-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: ${rem(80)};
        height: ${rem(80)};
      }

      .product-entry-related-products__card-title {
        max-width: ${rem(154)};
        font-size: ${rem(24)};
        line-height: ${rem(32)};
        color: var(--color-gray600);
      }
    }
  }

  .product-entry-related-products__link-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: ${rem(48)};

    @media ${media.midUp} {
      padding-top: ${rem(56)};
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(80)};
    }

    .product-entry-related-products__link {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${rem(8)};
      color: var(--color-primary);
      text-decoration: none;
    }

    .product-entry-related-products__link-icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--color-primary);
      border-radius: ${rem(32)};
    }

    .product-entry-related-products__link-text {
      @media ${media.stMidUp} {
        font-size: ${rem(20)};
      }
    }
  }
`
